import axios, {Axios, AxiosHeaders, AxiosInstance, AxiosRequestConfig, AxiosRequestHeaders} from "axios";
import {config as envConfig, config} from '@/config'

interface ClientConfig {
    credentials: boolean
    baseUrl: string
    headers: ClientHeader
}

interface ClientHeader {
    contentType: string
}

class BaseService {
    protected clientConfig: ClientConfig | undefined;
    protected clientType: 'axios' | 'fetch'

    protected axiosClient: AxiosInstance | undefined;
    protected axiosConfig: AxiosRequestConfig | undefined;

    protected fetchClient: any;

    public _retry = false;

    constructor(clientType: 'axios' | 'fetch' = 'axios') {
        this.clientType = clientType
        this.clientConfig = {
            credentials: true,
            baseUrl: config.api.url,
            headers: {
                contentType: "application/json"
            }
        }
        if (this.clientType == 'axios') {
            this.initAxiosClient()
        } else if (this.clientType == 'fetch') {
            this.initFetchClient()
        }
    }

    initAxiosClient = () => {
        this.axiosClient = axios.create({
            withCredentials: this.clientConfig?.credentials,
            baseURL: this.clientConfig?.baseUrl,
        })
        this.axiosConfig = {
            headers: {
                'Content-Type': this.clientConfig?.headers.contentType
            }
        }
        this.axiosClient.interceptors.response.use(
            (response) => response.data,
            async (error) => {
                const {response} = error;
                const originalRequest = error.config
                if (this.axiosClient && response && error.response.status == 401 && !this._retry) {
                    this._retry = true
                    console.log('retry base service')
                    try {
                        await this.axiosClient.post('/auth/refresh')
                        return this.axiosClient(originalRequest)
                    } catch (error) {
                        await this.axiosClient.get('/auth/clear-cookie')
                        return Promise.reject(error);
                    }
                }
                return Promise.reject(response);
            },
        );
    }

    initFetchClient = () => {
        this.fetchClient = {
            headers: {
                Accept: this.clientConfig?.headers.contentType,
                "Content-Type": this.clientConfig?.headers.contentType,
            },
        }
        if(this.clientConfig?.credentials){
            this.fetchClient.credentials = "include"
        }
    }

    fetchRequest = async (method:string, url: string, data?: any): Promise<any> => {
        let result: any;
        const baseUrl = this.clientConfig?.baseUrl
        const fullUrl = baseUrl + url
        const response = await fetch(
            fullUrl,{
                method,
                body: JSON.stringify(data),
                ...this.fetchClient,
            }
        )
        if (response.status == 401 && !this._retry) {
            this._retry = true
            const refreshed = await this.fetchRequestToken()
            if (refreshed) {
                return await this.fetchRequest(method, url, data);
            } else {
                throw new Error('Unauthorized: Refresh token expired');
            }
        }else{
            result = await response.json()
            return result
        }
    }

    fetchRequestToken = async () => {
        const baseUrl = this.clientConfig?.baseUrl
        const fullUrl = baseUrl + '/auth/refresh'
        const response = await fetch(
            fullUrl,{
                method: "POST",
                ...this.fetchClient,
                credentials: 'include',
                cache: 'no-cache'
            }
        )
        const refreshed = await response.json()
        console.log({refreshed})
        return response.status != 401
    }

    get = async (url: string) => {
        if(this.clientType == 'axios'){
            return await this.axiosClient?.get(url, this.axiosConfig)
        }else if(this.clientType == 'fetch'){
            const response = await this.fetchRequest('GET', url)
        }
    }

    post = async (url: string, data: any, isFormData: boolean = false) => {
        if(this.clientType == 'axios'){
            if(isFormData){
                return await this.axiosClient?.postForm(url, data, this.axiosConfig)
            }else{
                return await this.axiosClient?.post(url, data, this.axiosConfig)
            }
        }else if(this.clientType == 'fetch'){
            const response = await this.fetchRequest('POST', url, data)
        }
    }
}

export default BaseService;